import React, { useState } from "react";
import "./Loader.scss";

type LoaderProps = {
  visible?: boolean;
  message?: string;
  background?: string;
};

const Loader: React.FC<LoaderProps> = ({
  visible = true,
  message = undefined,
  background = "#f7f7f4b3",
}) => {
  // lista de mensagens padrão a usar quando não vier mensagem por parâmetro
  const defaultMessages = [
    "Adding some Mustard…",
    "Squeezing the bottle…",
    "Shaking the sauce…",
    "Warming up the grill…",
    "Toasting the buns…",
  ];

  // calculamos apenas uma vez por instância do componente para não trocar a cada render
  const [randomMessage] = useState(
    () => defaultMessages[Math.floor(Math.random() * defaultMessages.length)]
  );

  const displayedMessage =
    message && message.trim().length > 0 ? message : randomMessage;
  if (!visible) return null;

  return (
    <>
      <div
        className="mustard-loader-overlay"
        style={{ background }}
        aria-hidden={false}
      >
        <div
          className="mustard-loader-container"
          role="status"
          aria-live="polite"
          aria-label={displayedMessage}
        >
          <div className="mustard-loader-spinner" />

          <svg
            style={{ display: "none" }}
            className="scene"
            viewBox="0 0 226 173"
            width="226"
            height="173"
            aria-hidden="true"
          >
            <defs>
              {/* texturas simples */}
              <linearGradient id="cactusShade" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#bdbdbd" />
                <stop offset="100%" stopColor="#9e9e9e" />
              </linearGradient>
              <linearGradient id="rockShade" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0%" stopColor="#c7c7c7" />
                <stop offset="100%" stopColor="#a6a6a6" />
              </linearGradient>
              <clipPath id="sunGridClip">
                <circle cx="170" cy="28" r="22" />
              </clipPath>
              <pattern
                id="grid"
                width="8"
                height="8"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M8 0H0M0 0V8"
                  stroke="#1f1f1f"
                  strokeOpacity="0.35"
                  strokeWidth="0.8"
                />
              </pattern>
            </defs>

            {/* quadradinhos soltos (brilham) */}
            <g className="sparkles">
              <rect className="sparkle" x="24" y="20" width="5" height="5" />
              <rect className="sparkle" x="55" y="10" width="5" height="5" />
              <rect className="sparkle" x="120" y="40" width="5" height="5" />
              <rect className="sparkle" x="200" y="70" width="5" height="5" />
            </g>

            {/* Sol com grade */}
            <g className="sun">
              <circle cx="170" cy="28" r="22" fill="#ffe500" />
              <g clipPath="url(#sunGridClip)">
                <rect x="148" y="6" width="44" height="44" fill="url(#grid)" />
              </g>
            </g>

            {/* “prancha/colina” ao fundo */}
            <g className="back-plate">
              <ellipse
                cx="60"
                cy="68"
                rx="38"
                ry="22"
                fill="#bfbfbf"
                opacity="0.85"
              />
            </g>

            {/* rocha grande */}
            <g className="rock">
              <ellipse
                cx="95"
                cy="118"
                rx="42"
                ry="26"
                fill="url(#rockShade)"
              />
              <circle cx="75" cy="115" r="7" fill="#8f8f8f" />
              <circle cx="110" cy="130" r="5" fill="#8f8f8f" />
            </g>

            {/* cacto esquerdo */}
            <g className="cactus cactus-left">
              <path
                d="M30 145c0-30 6-50 12-50s12 20 12 50v15H30z"
                fill="url(#cactusShade)"
              />
              <rect
                x="26"
                y="90"
                width="8"
                height="28"
                rx="4"
                fill="url(#cactusShade)"
              />
              <rect
                x="54"
                y="98"
                width="8"
                height="20"
                rx="4"
                fill="url(#cactusShade)"
              />
              {/* pontas amarelas */}
              <rect x="38" y="89" width="8" height="8" rx="4" fill="#ffd500" />
              <rect x="26" y="82" width="8" height="8" rx="4" fill="#ffd500" />
              <rect x="54" y="92" width="8" height="8" rx="4" fill="#ffd500" />
            </g>

            {/* cacto direito */}
            <g className="cactus cactus-right">
              <path
                d="M185 148c0-26 5-43 9-43s9 17 9 43v12h-18z"
                fill="url(#cactusShade)"
              />
              <rect
                x="180"
                y="106"
                width="6"
                height="18"
                rx="3"
                fill="url(#cactusShade)"
              />
              <rect
                x="200"
                y="112"
                width="6"
                height="12"
                rx="3"
                fill="url(#cactusShade)"
              />
              <rect
                x="190"
                y="101"
                width="6"
                height="7"
                rx="3"
                fill="#ffd500"
              />
              <rect
                x="180"
                y="101"
                width="6"
                height="7"
                rx="3"
                fill="#ffd500"
              />
              <rect
                x="200"
                y="108"
                width="6"
                height="7"
                rx="3"
                fill="#ffd500"
              />
            </g>

            {/* tumbleweed */}
            <g className="tumbleweed">
              <circle
                cx="0"
                cy="132"
                r="18"
                fill="none"
                stroke="#9a9a9a"
                strokeWidth="2"
              />
              <circle
                cx="0"
                cy="132"
                r="12"
                fill="none"
                stroke="#9a9a9a"
                strokeWidth="1.5"
              />
              <path
                d="M-10 130c5 6 20 6 20 0M-14 136c8 7 28 7 36 0"
                fill="none"
                stroke="#9a9a9a"
                strokeWidth="1.2"
              />
            </g>

            {/* chão */}
            <rect x="0" y="150" width="226" height="23" fill="#b8936aff" />
          </svg>
          <div className="mustard-loader-message">{displayedMessage}</div>
        </div>
      </div>
    </>
  );
};

export default Loader;
